import { Spin, Space, Button, message, Tooltip, Divider } from "antd";
import { AiOutlineLogout, AiOutlineSetting } from "react-icons/ai";
import { FcAbout } from "react-icons/fc";
import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { deleteFcmToken } from "../../hooks/FCM/notification";
import { SessionStorage } from "../../services/storage";
import { AboutComponent } from "./AboutComponent";
import SettingComponent from "./SettingComponent";
import { useTableStore } from "../../stores/TableFilterStore";
import { useUserContextHook } from "../../hooks/useUserContextHook";
import { useNotificationStore } from "../../stores/notificationStore";

interface SignOutComponentProps { }

const SignOutComponent: React.FC<SignOutComponentProps> = () => {
	const { clearAll } = useTableStore();
	const { clearDetailHandle } = useUserContextHook();
	const { clearAll: clearNotificationStore, notificationToken } = useNotificationStore();

	const location = useLocation();
	const navigate = useNavigate();

	// Modal states
	const [aboutModal, setAboutModal] = useState<boolean>(false);
	const [settingModal, setSettingModal] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);

	/**
	 * Handles user sign out process
	 * Cleans up all application state and redirects to login
	 */
	const onSignOut = useCallback(async (): Promise<void> => {
		if (loading) return;

		try {
			setLoading(true);

			// Delete FCM token if exists
			if (notificationToken) {
				try {
					await deleteFcmToken();
				} catch (fcmError) {
					console.warn("Failed to delete FCM token:", fcmError);
					// Don't block sign out if FCM deletion fails
				}
			}

			// Clear all application state
			clearDetailHandle?.();
			clearAll();
			clearNotificationStore();

			// Clear all storage
			SessionStorage.clear();
			// localStorage.clear();
			sessionStorage.clear();

			// Show success message
			message.success("Signed out successfully");

			// Navigate to login with slight delay for better UX
			setTimeout(() => {
				navigate("/", { replace: true, state: { from: location } });
			}, 100);

		} catch (error) {
			console.error("Error during sign out:", error);
			message.error("An error occurred while signing out. Please try again.");

			// Still attempt to redirect even if there's an error
			setTimeout(() => {
				navigate("/", { replace: true });
			}, 1500);
		} finally {
			// Ensure loading state is cleared
			setTimeout(() => setLoading(false), 200);
		}
	}, [
		loading,
		notificationToken,
		clearAll,
		clearDetailHandle,
		clearNotificationStore,
		navigate,
		location
	]);

	const handleAboutModal = useCallback(() => {
		setAboutModal(true);
	}, []);

	const handleSettingModal = useCallback(() => {
		setSettingModal(true);
	}, []);

	const handleAboutClose = useCallback(() => {
		setAboutModal(false);
	}, []);

	const handleSettingClose = useCallback(() => {
		setSettingModal(false);
	}, []);

	return (
		<>
			{loading && (
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: 'rgba(0, 0, 0, 0.45)',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						zIndex: 9999
					}}
				>
					<Spin tip="Signing out..." size="large" />
				</div>
			)}

			<Space size={4} align="center">
				<Tooltip title="About" placement="bottom">
					<Button
						type="text"
						size="small"
						icon={<FcAbout size={18} style={{ display: 'flex' }} />}
						onClick={handleAboutModal}
						style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
					/>
				</Tooltip>

				<Tooltip title="Settings" placement="bottom">
					<Button
						type="text"
						size="small"
						icon={<AiOutlineSetting size={18} style={{ display: 'flex' }} />}
						onClick={handleSettingModal}
						style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
					/>
				</Tooltip>

				<Divider type="vertical" style={{ margin: '0 4px', height: 16, borderColor: 'rgba(255,255,255,0.3)' }} />

				<Tooltip title="Sign Out" placement="bottom">
					<Button
						type="primary"
						danger
						size="small"
						loading={loading}
						disabled={loading}
						onClick={onSignOut}
						icon={!loading ? <AiOutlineLogout size={14} style={{ display: 'flex' }} /> : undefined}
						style={{ display: 'flex', alignItems: 'center', gap: 4 }}
					>
						Sign Out
					</Button>
				</Tooltip>
			</Space>

			{/* About Modal */}
			{aboutModal && (
				<AboutComponent
					isOpen={aboutModal}
					setAboutModal={handleAboutClose}
				/>
			)}

			{/* Settings Modal */}
			{settingModal && (
				<SettingComponent
					isOpen={settingModal}
					setIsSetting={handleSettingClose}
				/>
			)}
		</>
	);
};

export default SignOutComponent;
